/**
 * Module dependencies,
 */

import { Container } from 'src/components/core/layout/container';
import { GetStaticProps, NextPage } from 'next';
import { Lead, Title } from 'src/components/layout/homepage/styles';
import { Locale } from 'i18n-routes';
import { ParallaxImage } from 'src/components/core/image';
import { RouterLink } from 'src/components/core/links/router-link';
import { SEO } from 'src/components/core/seo';
import { SSGProps } from 'src/types/app';
import { Svg } from 'src/components/core/svg';
import { TeamMember } from 'src/types/team-member';
import { Text } from 'src/components/core/text';
import { TriggerNavbarTheme } from 'src/components/layout/navbar/trigger-navbar-theme';
import { getTeam } from 'src/requests/team';
import { media } from 'src/styles/utils/media';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { useBreakpoint } from '@untile/react-components';
import { useRouteResolver } from 'src/hooks/use-route-resolver';
import { useTranslation } from 'next-i18next';
import arrowRight from 'src/assets/svg/arrow-right.svg';
import styled from 'styled-components';

/**
 * `Props` type.
 */

type Props = {
  data: {
    team: TeamMember[];
  };
};

/**
 * `Wrapper` styled component.
 */

const Wrapper = styled.section`
  color: var(--color-gray700);
  padding: calc(var(--navbar-height) + 3rem) 0 var(--vertical-spacing);
  position: relative;

  ${media.min.ms`
    padding: calc(var(--navbar-height) + 5rem) 0 var(--vertical-spacing);
  `}
`;

/**
 * `Grid` styled component.
 */

const Grid = styled.div`
  display: grid;
  grid-template-areas: 'title' 'lead' 'description' 'list';
  grid-template-columns: 1fr;

  ${media.min.ms`
    grid-template-areas: 
      'title title       .           .'
      'lead  lead        .           .'
      '.     description description .'
      'list  list        list        list';
    grid-template-columns: 1fr repeat(2, 4fr) 1fr;
  `}

  ${media.min.md`
    grid-template-columns: 2fr repeat(2, 3fr) 2fr;
  `}

  ${media.min.lg`
    grid-template-columns: 3fr 2fr 3fr 2fr;
  `}
`;

/**
 * `StyledLead` styled component.
 */

const StyledLead = styled(Lead)`
  grid-area: lead;
  margin-bottom: 2rem;

  ${media.min.md`
    margin-bottom: 3rem;
  `}
`;

/**
 * `Description` styled component.
 */

const Description = styled(Text).attrs({ as: 'p' })`
  grid-area: description;
  margin-bottom: 5rem;

  ${media.min.md`
    margin-bottom: 3rem;
  `}
`;

/**
 * `List` styled component.
 */

const List = styled.div`
  --grid-row-gap: 2.5rem;

  display: grid;
  grid-area: list;
  grid-row-gap: var(--grid-row-gap);
  grid-template-columns: 1fr;

  ${media.min.md`
    --grid-row-gap: 4rem;

    grid-template-columns: repeat(2, 1fr);
  `}
`;

/**
 * `CardContent` styled component.
 */

const CardContent = styled.div`
  padding: 1rem;

  ${media.min.sm`
    align-self: flex-end;
  `}
`;

/**
 * `Name` styled component.
 */

const Name = styled(Text).attrs({
  block: true,
  bold: true,
  variant: 'h5'
})`
  transition: color var(--transition-default);
`;

/**
 * `ArrowRight` styled component.
 */

const ArrowRight = styled(Svg).attrs({
  icon: arrowRight,
  size: '48px'
})`
  transform: translateX(0);
  transition: var(--transition-default);
  transition-property: color, transform;
`;

/**
 * `Card` styled component.
 */

const Card = styled(RouterLink)`
  display: grid;
  grid-template-columns: 1fr;

  ${media.min.sm`
    grid-template-columns: 1fr 0.67fr;
  `}

  &:focus-within,
  &:hover {
    ${Name} {
      color: var(--color-sage500);
    }

    ${ArrowRight} {
      color: var(--color-sage500);
      transform: translateX(1rem);
    }
  }
`;

/**
 * `ImageWrapper` styled component.
 */

const ImageWrapper = styled.div`
  overflow: hidden;
  padding-top: 100%;
  position: relative;
`;

/**
 * `Team` page.
 */

const Team: NextPage<Props> = (props: Props) => {
  const { t } = useTranslation(['common', 'team']);
  const routeResolve = useRouteResolver();
  const isTablet = useBreakpoint('sm');
  const gridItemsPerRow = (isTablet && 2) || 1;

  return (
    <>
      <SEO />

      <TriggerNavbarTheme theme={'light'} />

      <Wrapper>
        <Container>
          <Grid>
            <Title
              as={'h1'}
              data-scroll={'fade-in-up'}
              style={{ gridArea: 'title' }}
            >
              {t('team:list.lead')}
            </Title>

            <StyledLead
              as={'h2'}
              data-scroll={'fade-in-up'}
              data-scroll-animation-delay={100}
            >
              {t('team:list.title')}
            </StyledLead>

            <Description
              data-scroll={'fade-in-up'}
              data-scroll-animation-delay={200}
            >
              {t('team:list.description')}
            </Description>

            <List>
              {props.data.team.map(({ image, name, role, slug }, index) => (
                <Card
                  data-scroll={'fade-in-up'}
                  data-scroll-animation-delay={((index % gridItemsPerRow) + 1) * 200}
                  data-scroll-offset={'20%'}
                  href={routeResolve('teamMember', { slug })}
                  key={image + index}
                >
                  <ImageWrapper>
                    <ParallaxImage
                      alt={`${name} image`}
                      src={image}
                      style={{ objectPosition: 'top center' }}
                    />
                  </ImageWrapper>

                  <CardContent>
                    {role && (
                      <Text
                        block
                        variant={'small'}
                      >
                        {role}
                      </Text>
                    )}

                    <Name as={'h3'}>{name}</Name>

                    <ArrowRight />
                  </CardContent>
                </Card>
              ))}
            </List>
          </Grid>
        </Container>
      </Wrapper>
    </>
  );
};

/**
 * Export `getStaticProps`.
 */

export const getStaticProps: GetStaticProps<SSGProps> = async ({ locale }) => {
  const team = await getTeam(locale as Locale);

  return {
    props: {
      data: { team },
      navbarTheme: 'light',
      ...(await serverSideTranslations(locale as string, ['common', 'team']))
    },
    revalidate: 60
  };
};

/**
 * Export `Team` component.
 */

export default Team;
